import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Crown, ArrowLeft, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MembershipSuccess = () => {
  const [searchParams] = useSearchParams();
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');
  const token = localStorage.getItem('token');

  // Determine initial status based on params
  const initialStatus = (!sessionId || !token) ? 'error' : 'loading';
  const [status, setStatus] = useState(initialStatus);

  useEffect(() => {
    if (initialStatus === 'error') {
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setPaymentData(data);

          if (data.payment_status === 'paid' && data.processed) {
            setStatus('success');
            // Update local storage user data
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            if (userData) {
              userData.membership_tier = data.plan_id;
              userData.membership_type = data.plan_id;
              localStorage.setItem('user', JSON.stringify(userData));
            }
          } else if (data.status === 'expired' || data.payment_status === 'unpaid') {
            setStatus('failed');
          } else if (pollCount < 10) {
            setTimeout(() => setPollCount(prev => prev + 1), 2000);
          } else {
            setStatus('pending');
          }
        } else {
          setStatus('error');
        }
      } catch (err) {
        console.error('Error checking payment:', err);
        if (pollCount < 10) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, token, pollCount]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6 text-center">
          {status === 'loading' && (
            <>
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-purple-600 animate-spin" />
              </div>
              <h2 className="text-xl font-bold mb-2">Processing Payment...</h2>
              <p className="text-gray-500">Please wait while we upgrade your membership</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-6">
                <Crown className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-xl font-bold text-green-600 mb-2">Membership Upgraded!</h2>
              <p className="text-gray-500 mb-4">
                Welcome to your new membership! You now have access to premium features.
              </p>
              {paymentData && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 mb-6 text-left border border-purple-200">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Amount Paid</span>
                    <span className="font-medium">{formatPrice(paymentData.amount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">New Plan</span>
                    <span className="text-purple-600 font-medium capitalize">
                      {paymentData.plan_id || 'Premium'}
                    </span>
                  </div>
                </div>
              )}
              <div className="space-y-3">
                <Link to="/dashboard" className="block">
                  <Button className="w-full bg-gradient-to-r from-purple-600 to-indigo-600">
                    <Crown className="w-4 h-4 mr-2" />
                    Go to Dashboard
                  </Button>
                </Link>
                <Link to="/membership" className="block">
                  <Button variant="outline" className="w-full">
                    View Membership Benefits
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-red-600 mb-2">Payment Failed</h2>
              <p className="text-gray-500 mb-6">
                Your payment could not be processed. Please try again.
              </p>
              <div className="space-y-3">
                <Link to="/membership" className="block">
                  <Button className="w-full">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'pending' && (
            <>
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-yellow-600" />
              </div>
              <h2 className="text-xl font-bold text-yellow-600 mb-2">Payment Pending</h2>
              <p className="text-gray-500 mb-6">
                Your payment is still being processed. Please check back later.
              </p>
              <Link to="/membership" className="block">
                <Button className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Membership
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-600 mb-2">Something Went Wrong</h2>
              <p className="text-gray-500 mb-6">
                We could not verify your payment. Please contact support if you were charged.
              </p>
              <Link to="/membership" className="block">
                <Button className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Membership
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MembershipSuccess;
